// Ported from dev-scrips-pm-react src/patients/services/use-vaccine-code-search.ts (PROD-1119 Patients data layer).
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ImmunizationCoding } from '@/types/patient-chart-api';

/**
 * Master data — CVX vaccine code typeahead for the Immunizations
 * Add flow (PROD-827 chart write surfaces).
 *
 * Wire (curl-verified by Andrew 2026-05-20):
 *
 *   GET /api/Master/VaccinationCodesList?searchQuery={query}
 *       → ImmunizationCoding[]
 *
 * Each row carries the 6-field shape including PascalCase `Definition`.
 * `system` is the CVX code system URL (`http://hl7.org/fhir/sid/cvx`).
 * The Create/Update wire bodies ship `vaccine` with this exact shape
 * verbatim — never strip or rename fields.
 *
 * Stale-request safety: same four-layer defense as the other master
 * search hooks (allergies / diagnosis / procedures / social history).
 */

const MIN_QUERY_LENGTH = 1;

export function useVaccineCodeSearch(query: string, enabled = true) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['master-vaccine-codes', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<ImmunizationCoding[]>(
        `/api/Master/VaccinationCodesList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: enabled && trimmed.length >= MIN_QUERY_LENGTH,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}
